import React from "react";

import OrderTypeInfo from "./OrderTypeInfo";
import TargetGeneralInfo from "./TargetUserInfo";
import PaymentUrl from "./PaymentUrl";
import GeneralInfoDeliveryAndPaymentDetails from "./DeliveryAndPayment";
import {
    cancelOrder as cancelOrderRequest,
    returnOrder as returnOrderRequest,
    getOrder as getOrderRequest,
    updateOrder as updateOrderRequest,
} from "@admin/domain/orders/api";
import { useOrderContext } from "../OrderContext";
import { OrderStatus, readableOrderStatus } from "@admin/domain/orders/enums/OrderStatus";
import { ResponseErrorInterface } from "@admin/domain/api/ResponseErrorInterface";
import CreatorInfo from "@admin/pages/OrderEditor/GeneralInfo/CreatorInfo";
import OrderChangesFromDistributorModal from "@admin/pages/OrderEditor/GeneralInfo/DistributorOrderChangesModal";
import SbpAdditionalPaymentModal from "@admin/pages/OrderEditor/GeneralInfo/SbpAdditionalPaymentModal";
import { EditOrderData } from "@admin/domain/orders/dto/EditOrderData";
import { DateTime } from "luxon";
import { useLoaderContext } from "@admin/components/LoaderContext";
import compareOrders from "@admin/domain/orders/compareOrders";
import { useOrderHooks } from "@admin/pages/OrderEditor/hooks";
import { prettyDate } from "@admin/utils/dates";

export default function GeneralInfo() {
    const { order, setOrder } = useOrderContext();
    const { orderRequest } = useOrderHooks();
    const { isLoading, startLoading, stopLoading, setErrorsFromResponse, request } = useLoaderContext();
    const [orderChanges, setOrderChanges] = React.useState<Map<string, [string, string]>>(new Map());
    const [actualOrder, setActualOrder] = React.useState<EditOrderData | null>(null);
    const [sbpModalIsOpened, setSbpModalIsOpened] = React.useState<boolean>(false);

    const checkOrderChangesAndUpdate = async (): Promise<void> => {
        startLoading();

        try {
            const { order: actualOrder } = await getOrderRequest(order.id);

            if (
                (actualOrder.edited_by_distributor_at &&
                    DateTime.fromSQL(order.updated_at) < DateTime.fromSQL(actualOrder.edited_by_distributor_at)) ||
                (actualOrder.shipping_info?.edited_by_distributor_at &&
                    order.shipping_info?.updated_at &&
                    DateTime.fromSQL(order.shipping_info.updated_at) <
                    DateTime.fromSQL(actualOrder.shipping_info.edited_by_distributor_at))
            ) {
                const newOrderChanges = compareOrders(order, actualOrder);

                if (newOrderChanges.size > 0) {
                    setOrderChanges(newOrderChanges);
                    setActualOrder(actualOrder);
                } else {
                    await updateOrder();

                    return;
                }

                stopLoading();
                return;
            }

            await updateOrder();
            stopLoading();
        } catch (e) {
            setErrorsFromResponse(e as ResponseErrorInterface);
        }
    };

    const updateOrder = async (): Promise<void> => {
        orderRequest(async (): Promise<void> => {
            await updateOrderRequest(order);
        });
    };

    const cancelOrder = async (): Promise<void> => {
        request(async (): Promise<void> => {
            await cancelOrderRequest(order.id);
            location.reload();
        });
    };

    const actualizeOrder = (): void => {
        setOrderChanges(new Map());
        setOrder(actualOrder as EditOrderData);
    };

    const returnOrder = (): void => {
        const message = order.is_parent
            ? `Вернуть консолидированный заказ и все его подзаказы?`
            : `Вернуть заказ?`;
        if (!confirm(message)) {
            return;
        }
        orderRequest(async (): Promise<void> => {
            await returnOrderRequest(order.id);
        });
    };

    const overwriteOrder = async (): Promise<void> => {
        setOrderChanges(new Map());
        await updateOrder();
    };

    return (
        <div className="rounded border p-5 mb-5">
            {orderChanges.size > 0 && actualOrder && (
                <OrderChangesFromDistributorModal
                    orderChanges={orderChanges}
                    closeModal={(): void => setOrderChanges(new Map())}
                    actualizeOrder={actualizeOrder}
                    overwriteOrder={overwriteOrder}
                    actualOrder={actualOrder}
                />
            )}

            {sbpModalIsOpened && (
                <SbpAdditionalPaymentModal
                    orderId={order.id}
                    closeModal={(): void => setSbpModalIsOpened(false)}
                />
            )}

            <h4 className="mb-5 ms-3 cursor-default">Общие сведения</h4>

            <div className="row row-cols-1 row-cols-sm-2 row-cols-xl-3 row-cols-xxl-4">
                <div className="col p-6 cursor-default">
                    <div>
                        <span className="fw-bolder">Номер заказа: </span>
                        {` `}
                        {order.id || ``}
                    </div>
                    <div>
                        <span className="fw-bolder">Дата оформления: </span>
                        {` `}
                        {prettyDate(order.created_at)}
                    </div>
                    <div>
                        <span className="fw-bolder">Статус заказа: </span>
                        {` `}
                        {readableOrderStatus(order.status)}
                    </div>
                    <div>
                        <OrderTypeInfo order={order} />
                    </div>
                    <div>
                        <span className="fw-bolder">Источник заказа: </span>
                        {` `}
                        {(order.from_mobile_app ? `Мобильное приложение` : `Веб сайт`) || ``}
                    </div>
                    <div>
                        <span className="fw-bolder">Трек номер: </span>
                        {` `}
                        {(order.track_url)}
                    </div>
                </div>

                <CreatorInfo />

                {!order.is_parent && <TargetGeneralInfo />}

                <GeneralInfoDeliveryAndPaymentDetails />

                <div className="col p-6 cursor-default mw-250px">
                    <div className="mb-2">
                        <PaymentUrl order={order} />
                    </div>
                    <div>
                        <a className="d-block w-max-content" href={`/admin/orders/${order.id}/invoice`} target="_blank">
                            Сгенерировать накладную
                        </a>
                    </div>
                    <div className="mt-2">
                        <button
                            className="btn btn-info"
                            onClick={(): void => setSbpModalIsOpened(true)}
                            disabled={isLoading}
                        >
                            Доплата СБП
                        </button>
                    </div>
                    {order.is_editable && (
                        <div className="d-flex flex-column mt-10 gap-5">
                            <button className="btn btn-danger" onClick={cancelOrder} disabled={isLoading}>
                                Отменить заказ
                            </button>
                            <button className="btn btn-primary" onClick={checkOrderChangesAndUpdate}
                                disabled={isLoading}>
                                Сохранить
                            </button>
                        </div>
                    )}
                    {![OrderStatus.Returned, OrderStatus.Cancelled, OrderStatus.Deleted].includes(order.status) && (
                        <div className="mt-5">
                            <button className="btn btn-danger" onClick={returnOrder} disabled={isLoading}>
                                Вернуть заказ
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
